import { useMemo, useState } from 'react'
import { Check, Plus, Scales } from '@phosphor-icons/react'
import { useFormat } from '@/i18n'
import {
  Badge,
  Banner,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  type Column,
} from '@/ui'
import { cn } from '@/lib/cn'
import { useCan } from '@/auth/context'
import { errorMessage } from '../api/core'
import {
  dueState,
  useEquipment,
  useSaveEquipment,
  type DueState,
  type EquipmentRow,
} from '../api/setup'
import { useSites } from '../api/store'
import { useSteps } from '../api/processing'
import { ActionButton } from '../components/Can'
import { BoolFilter, FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code, RefLink, Status } from '../components/bits'
import { humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'
import {
  emptyEquipmentForm,
  equipmentBody,
  equipmentFormOf,
  EQUIPMENT_KIND_LABEL,
  EQUIPMENT_KINDS,
  GATE_KIND_LABEL,
  isMachine,
  MACHINE,
  stepCodesOf,
  validateEquipmentForm,
  type EquipmentForm,
  type EquipmentKind,
  type GateKind,
} from '../model/equipmentForm'
import { useScope } from '../api/appConfig'

const LIMIT = 25

/** V1 has no scale test, so no test weights to register (they stay listed if any exist). */
function useEquipmentKinds() {
  const { v1 } = useScope()
  return v1 ? EQUIPMENT_KINDS.filter((k) => k !== 'TEST_WEIGHT') : EQUIPMENT_KINDS
}

const kindLabel = (r: Pick<EquipmentRow, 'gateKind' | 'category' | 'specifications'>) =>
  r.gateKind && r.gateKind in GATE_KIND_LABEL
    ? GATE_KIND_LABEL[r.gateKind as GateKind]
    : isMachine(r)
      ? EQUIPMENT_KIND_LABEL.MACHINE
      : humanize(r.category)

/**
 * Equipment (F4.1, R30): gate scales, moisture meters and test weights with their calibration and
 * stamp dates; processing machines (F6.9) with the steps the phones offer them for.
 */
export function EquipmentPage() {
  const kinds = useEquipmentKinds()
  const [f, setF, { reset, active }] = useUrlState({
    gateKind: '',
    isActive: undefined as boolean | undefined,
    page: 1,
  })
  const [editing, setEditing] = useState<EquipmentRow | 'new' | null>(null)
  const can = useCan()
  const sites = useSites()
  const siteName = useMemo(
    () => new Map((sites.data ?? []).map((s) => [s.id, s.name])),
    [sites.data],
  )
  // Machines have no gate kind: their filter is the PROCESSING category.
  const q = useEquipment({
    page: f.page,
    limit: LIMIT,
    ...(f.gateKind === MACHINE
      ? { category: 'PROCESSING' }
      : { gateKind: f.gateKind || undefined }),
    isActive: f.isActive,
  })
  const rows = q.data?.rows ?? []
  const lapsed = rows.filter(
    (r) =>
      r.isActive &&
      (dueState(r.stampDueAt) === 'LAPSED' || dueState(r.calibrationDueAt) === 'LAPSED'),
  )

  const columns: Column<EquipmentRow>[] = [
    {
      id: 'kind',
      header: 'Kind',
      cell: (r) => {
        const steps = stepCodesOf(r.specifications)
        return (
          <span className="flex flex-col">
            {kindLabel(r)}
            {isMachine(r) && (
              <span
                className={cn(
                  'font-mono text-[11.5px]',
                  steps.length ? 'text-muted' : 'text-danger',
                )}
              >
                {steps.length ? steps.join(' · ') : 'No steps — not on the phones'}
              </span>
            )}
          </span>
        )
      },
    },
    {
      id: 'item',
      header: 'Code · name',
      cell: (r) => (
        <span className="flex flex-col">
          <Code>{r.code}</Code>
          <span className="text-[13px] text-muted">
            {r.name}
            {r.serialNo && <> · S/N {r.serialNo}</>}
          </span>
          {isMachine(r) && (
            // the machine processing log, filtered to this machine
            <span className="text-[12.5px]" onClick={(e) => e.stopPropagation()}>
              <RefLink to={`/c/processing/runs?equipmentId=${r.id}`}>Runs</RefLink>
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'place',
      header: 'Kept at',
      cell: (r) =>
        r.locationId ? (
          (siteName.get(r.locationId) ?? '—')
        ) : (
          <span className="text-muted">Any village</span>
        ),
      hideBelow: 'lg',
    },
    {
      id: 'calibration',
      header: 'Calibration due',
      cell: (r) => (
        <DueCell
          date={r.calibrationDueAt}
          since={r.calibratedAt}
          sinceLabel="calibrated"
          active={r.isActive}
        />
      ),
      hideBelow: 'md',
    },
    {
      id: 'stamp',
      header: 'Stamp due',
      cell: (r) => (
        <span className="flex flex-col">
          <DueCell
            date={r.stampDueAt}
            since={r.stampedAt}
            sinceLabel="stamped"
            active={r.isActive}
          />
          {r.legalMetrologyCertNo && (
            <span className="font-mono text-[11.5px] text-muted">
              Cert {r.legalMetrologyCertNo}
            </span>
          )}
        </span>
      ),
      hideBelow: 'sm',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <Status
          status={r.isActive ? 'ACTIVE' : 'DISABLED'}
          label={r.isActive ? 'In use' : 'Retired'}
        />
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Equipment"
      description="Gate scales, moisture meters, test weights and processing machines. A scale whose Legal Metrology stamp has lapsed shows red on the phone; a machine shows on the process phone only for the steps ticked here."
      actions={
        <ActionButton
          perm="equipment:create"
          leadingIcon={<Plus />}
          onClick={() => setEditing('new')}
        >
          Add equipment
        </ActionButton>
      }
    >
      {lapsed.length > 0 && (
        <Banner
          tone="error"
          title={`${lapsed.length} ${lapsed.length === 1 ? 'item is' : 'items are'} past the stamp or calibration date`}
        >
          {lapsed.map((r) => r.code).join(', ')} — get them re-stamped or calibrated, then update
          the dates here.
        </Banner>
      )}
      <DataTable<EquipmentRow>
        caption="Equipment"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        onRowClick={can('equipment:update') ? (r) => setEditing(r) : undefined}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Kind"
              value={f.gateKind as EquipmentKind | ''}
              onChange={(gateKind) => setF({ gateKind })}
              options={kinds.map((k) => ({ value: k, label: EQUIPMENT_KIND_LABEL[k] }))}
            />
            <BoolFilter
              label="In use"
              value={f.isActive}
              onChange={(isActive) => setF({ isActive })}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Scales />}
            title={active ? 'Nothing matches' : 'No equipment yet'}
            description={
              active
                ? 'Try clearing the filters.'
                : 'Add each gate scale and moisture meter with its serial number and stamp dates.'
            }
          />
        }
      />
      <EquipmentDialog item={editing} onClose={() => setEditing(null)} />
    </ConsolePage>
  )
}

const DUE_TONE: Record<DueState, string> = {
  LAPSED: 'text-danger font-medium',
  SOON: 'text-warn font-medium',
  OK: 'text-ink',
  NONE: 'text-muted',
}

function DueCell({
  date,
  since,
  sinceLabel,
  active,
}: {
  date: string | null
  since: string | null
  sinceLabel: string
  active: boolean
}) {
  const fmt = useFormat()
  const state = active ? dueState(date) : 'NONE'
  if (!date)
    return <span className="text-muted">{since ? `${sinceLabel} ${fmt.date(since)}` : '—'}</span>
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className={cn('whitespace-nowrap', DUE_TONE[state])}>{fmt.date(date)}</span>
      {state === 'LAPSED' && <Badge tone="danger">Lapsed</Badge>}
      {state === 'SOON' && <Badge tone="warn">Due soon</Badge>}
    </span>
  )
}

function EquipmentDialog({
  item,
  onClose,
}: {
  item: EquipmentRow | 'new' | null
  onClose: () => void
}) {
  const kinds = useEquipmentKinds()
  const isNew = item === 'new'
  const [form, setForm] = useState<EquipmentForm>(emptyEquipmentForm)
  const [touched, setTouched] = useState(false)
  const [was, setWas] = useState(item)
  if (item !== was) {
    setWas(item)
    if (item) {
      setForm(item === 'new' ? emptyEquipmentForm() : equipmentFormOf(item))
      setTouched(false)
    }
  }
  const sites = useSites()
  const steps = useSteps()
  const save = useSaveEquipment()
  const errors = validateEquipmentForm(form, isNew)
  const valid = Object.keys(errors).length === 0
  const set = (patch: Partial<EquipmentForm>) => setForm((f) => ({ ...f, ...patch }))
  const err = (k: keyof EquipmentForm) => (touched ? errors[k] : null)
  const machine = form.gateKind === MACHINE || form.stepCodes.length > 0
  const known = new Set((steps.data ?? []).map((st) => st.code))
  const unknownSteps = steps.data ? form.stepCodes.filter((c) => !known.has(c)) : []
  const machineSites = (sites.data ?? []).filter(
    (s) => s.kind === 'PROCESSING' || s.kind === 'PACKING',
  )
  const siteOptions = machine && machineSites.length ? machineSites : (sites.data ?? [])
  const date = (
    k: 'calibratedAt' | 'calibrationDueAt' | 'stampedAt' | 'stampDueAt',
    label: string,
  ) => (
    <Field label={label} error={err(k)}>
      <Input type="date" value={form[k]} onChange={(e) => set({ [k]: e.target.value })} />
    </Field>
  )

  return (
    <FormDialog
      open={item !== null}
      onClose={onClose}
      title={item && item !== 'new' ? `Edit ${item.code}` : 'Add equipment'}
      description={
        isNew
          ? 'Scales need the Legal Metrology stamp dates; the phone warns once the stamp is due.'
          : undefined
      }
      saveLabel={isNew ? 'Add' : 'Save'}
      loading={save.isPending}
      onSave={() => {
        setTouched(true)
        if (!valid) throw new Error('invalid')
        const row = item && item !== 'new' ? item : null
        return save.mutateAsync({
          id: row?.id,
          body: equipmentBody(form, isNew, row?.specifications),
        })
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Kind" required={isNew} error={err('gateKind')}>
          <Select<EquipmentKind>
            value={form.gateKind}
            onChange={(gateKind) => set({ gateKind })}
            placeholder="Choose…"
            options={kinds.map((k) => ({ value: k, label: EQUIPMENT_KIND_LABEL[k] }))}
          />
        </Field>
        <Field
          label="Code"
          required
          error={err('code')}
          hint={machine ? 'Unique, at most 8 characters, e.g. DH-01' : 'Unique, e.g. SC-UDAI-01'}
        >
          <Input
            value={form.code}
            onChange={(e) => set({ code: e.target.value })}
            maxLength={50}
            className="font-mono"
            data-autofocus
          />
        </Field>
        <Field label="Name" required error={err('name')}>
          <Input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            maxLength={255}
            placeholder="e.g. Platform scale 100 kg"
          />
        </Field>
        <Field label="Serial number" error={err('serialNo')}>
          <Input
            value={form.serialNo}
            onChange={(e) => set({ serialNo: e.target.value })}
            maxLength={60}
            className="font-mono"
          />
        </Field>
        {date('calibratedAt', 'Calibrated on')}
        {date('calibrationDueAt', 'Calibration due')}
        {date('stampedAt', 'Stamped on')}
        {date('stampDueAt', 'Stamp due')}
        <Field label="Legal Metrology certificate no." error={err('legalMetrologyCertNo')}>
          <Input
            value={form.legalMetrologyCertNo}
            onChange={(e) => set({ legalMetrologyCertNo: e.target.value })}
            maxLength={40}
            className="font-mono"
            placeholder="e.g. LM/UDR/2026/0412"
          />
        </Field>
        <Field
          label="Kept at"
          hint={
            machine
              ? 'A machine sits at a processing (or packing) site.'
              : form.locationId
                ? 'Offered only on the phones of this place. Choose another place to move it.'
                : 'Not fixed: offered on every village phone.'
          }
        >
          <Select
            value={form.locationId}
            onChange={(locationId) => set({ locationId })}
            placeholder={sites.isLoading ? 'Loading…' : 'Any village'}
            options={siteOptions.map((s) => ({ value: s.id, label: s.name }))}
          />
        </Field>
      </div>
      {machine && (
        <Field
          label="Steps it runs"
          required={isNew}
          error={err('stepCodes')}
          hint="The process phone offers this machine only for the ticked steps. Each step also needs a yield band (Processing › Yield bands)."
        >
          <div role="group" className="flex flex-wrap gap-2">
            {steps.isLoading && <span className="text-[14px] text-muted">Loading steps…</span>}
            {steps.isError && (
              <span className="text-[14px] text-danger">
                Steps could not be loaded: {errorMessage(steps.error)}
              </span>
            )}
            {/* known steps first, then any code on the row the registry no longer has (kept unless unticked) */}
            {[
              ...(steps.data ?? []).map((st) => ({ code: st.code, label: st.label })),
              ...unknownSteps.map((code) => ({ code, label: code })),
            ].map((st) => {
              const on = form.stepCodes.includes(st.code)
              return (
                <Button
                  key={st.code}
                  size="sm"
                  variant={on ? 'secondary' : 'ghost'}
                  aria-pressed={on}
                  leadingIcon={on ? <Check /> : undefined}
                  onClick={() =>
                    set({
                      stepCodes: on
                        ? form.stepCodes.filter((c) => c !== st.code)
                        : [...form.stepCodes, st.code],
                    })
                  }
                >
                  {st.label}
                </Button>
              )
            })}
          </div>
        </Field>
      )}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            checked={form.sharedUse}
            onChange={(e) => set({ sharedUse: e.target.checked })}
            className="h-4 w-4 accent-amber"
          />
          Shared — moves between villages
        </label>
        {!isNew && (
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
              className="h-4 w-4 accent-amber"
            />
            In use (untick to retire it; phones stop offering it)
          </label>
        )}
      </div>
    </FormDialog>
  )
}
